import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { ArrowLeft, Building2, FileClock, LineChart, Lock, Unlock, Users, XCircle, History } from 'lucide-react';
import AppPageHeader from '../shell/AppPageHeader';
import { useFirmMainHeaderSlot } from './FirmMainHeaderContext';
import { CompanyIntakeBadges } from '../shared/CompanySummaryCard';
import ManagedUserDirectoryPanel from '../shared/ManagedUserDirectoryPanel';
import { ClientEmptyStateCard } from '../shared/ClientEmptyStateCard';
import { CompanyProvider, useCompanyContext } from '../client';
import { WorkflowHomePage } from '../client/workflow/home/WorkflowHomePage';
import { InfoBanner, Badge } from '../client/components/WorkflowFormPrimitives';
import {
  RunHistoryPanel,
  RunSnapshotSlideOver,
  filterRunHistoryTableRows,
  mapFirmCompanyRunHistoryToTableRows,
  runHistoryRowToSnapshotSlideSubject,
  type RunSnapshotSlideSubject,
} from '../client/runHistory';
import { useAuth } from '../../contexts/AuthContext';
import { useFirmCompanies } from './FirmCompaniesContext';
import {
  fetchCompanyById,
  fetchCompanyAnalyses,
  patchCompany,
  type CompanyRecord,
} from '../../services/FirmCompaniesApiClient';
import { createCompanyClientUsersAdapter } from '../../services/managedDirectoryUsersApi';
import {
  DEAL_ROLE_OPTIONS,
  DEAL_STRUCTURE_OPTIONS,
  LISTING_STATUS_OPTIONS,
  WORK_STATUS_OPTIONS,
} from './companyIntakeFields';
import {
  FirmAnalysisPersistedLockMark,
  FirmLiveAnalysisExpandChevron,
  FirmLiveAnalysisRunHistoryPanel,
  FirmLiveAnalysisWorkflowStatusBadge,
} from './FirmLiveAnalysisTableParts';
import {
  formatFirmAnalysisAfr,
  formatFirmAnalysisDealPrice,
  formatFirmAnalysisUpdatedSubtitle,
} from './firmLiveAnalysisTableUtils';
import { isAnalysisLocked } from '../../constants/analysisStatus';
import { shellMainPaddingX } from '../../utils/shellLayoutClasses';
import { canManageUsers } from '../../utils/canManageUsers';

type CompanyDetailTab = 'analyses' | 'historicalRuns' | 'companyUsers';

function AnalysisSlideWorkflow({ analysisId }: { analysisId: string }) {
  const { analyses, setCurrentAnalysisId, data, setData } = useCompanyContext();

  useEffect(() => {
    if (!analysisId) return;
    if (analyses.some((a) => a.id === analysisId)) setCurrentAnalysisId(analysisId);
  }, [analysisId, analyses, setCurrentAnalysisId]);

  return (
    <WorkflowHomePage
      key={analysisId}
      formData={data}
      setFormData={setData}
      initialStepId="transaction"
      continueMode="dual"
    />
  );
}

export default function CompanyDetail() {
  const navigate = useNavigate();
  const location = useLocation();
  const openedCompanyEditFromList = useRef(false);
  const { id } = useParams<{ id: string }>();
  const { lawFirmId, refreshCompanies } = useFirmCompanies();
  const { user } = useAuth();
  const [company, setCompany] = useState(null as CompanyRecord | null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null as string | null);
  const [activeTab, setActiveTab] = useState('analyses' as CompanyDetailTab);
  const [expandedLiveAnalysisIds, setExpandedLiveAnalysisIds] = useState([] as string[]);
  const [historicalSearchQuery, setHistoricalSearchQuery] = useState('');
  const [historicalRunsLoading, setHistoricalRunsLoading] = useState(false);
  const [historicalRunsError, setHistoricalRunsError] = useState(null as string | null);
  const [directoryPanelsCloseKey, setDirectoryPanelsCloseKey] = useState(0);
  const [companyEditOpen, setCompanyEditOpen] = useState(false);
  const [editCompanyName, setEditCompanyName] = useState('');
  const [editClosingDate, setEditClosingDate] = useState('');
  const [editDealStructure, setEditDealStructure] = useState('unsure');
  const [editDealRole, setEditDealRole] = useState('unsure');
  const [editListingStatus, setEditListingStatus] = useState('unsure');
  const [editWorkStatus, setEditWorkStatus] = useState('intake');
  const [editNotes, setEditNotes] = useState('');
  const [companyEditError, setCompanyEditError] = useState(null as string | null);
  const [companyEditSubmitting, setCompanyEditSubmitting] = useState(false);

  const [analysisSlideOpen, setAnalysisSlideOpen] = useState(false);
  const [analysisSlideAnalysisId, setAnalysisSlideAnalysisId] = useState(null as string | null);

  const [runSnapshotSubject, setRunSnapshotSubject] = useState(null as RunSnapshotSlideSubject | null);

  const applyCompanyToEditForm = useCallback((c: CompanyRecord) => {
    setCompanyEditError(null);
    setEditCompanyName(c.companyName ?? '');
    const cd = (c.closingDate ?? '').trim();
    setEditClosingDate(cd.length >= 10 ? cd.slice(0, 10) : cd);
    setEditDealStructure(c.dealStructure ?? 'unsure');
    setEditDealRole(c.dealRole ?? 'unsure');
    setEditListingStatus(c.listingStatus ?? 'unsure');
    setEditWorkStatus(c.workStatus?.trim() || 'intake');
    setEditNotes(c.notes ?? '');
  }, []);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    fetchCompanyById(lawFirmId, id)
      .then((row) => {
        if (!cancelled) setCompany(row);
      })
      .catch((e) => {
        if (!cancelled) {
          setCompany(null);
          setLoadError(e instanceof Error ? e.message : 'Failed to load company');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [lawFirmId, id]);

  /** Load live analyses for this law firm + company (dedicated API — not the full company GET). */
  const refreshAnalysesForCompanyTab = useCallback(async () => {
    if (!id) return;
    setHistoricalRunsLoading(true);
    setHistoricalRunsError(null);
    try {
      const payload = await fetchCompanyAnalyses(lawFirmId, id);
      setCompany((prev) =>
        prev
          ? {
              ...prev,
              analysesCount: payload.analysesCount,
              analysisRuns: payload.analysisRuns,
              analyses: payload.analyses ?? [],
            }
          : prev
      );
    } catch (e: unknown) {
      setHistoricalRunsError(e instanceof Error ? e.message : 'Failed to load run history');
      /* keep existing company on refresh failure */
    } finally {
      setHistoricalRunsLoading(false);
    }
  }, [lawFirmId, id]);

  useEffect(() => {
    if (!id || !company) return;
    refreshAnalysesForCompanyTab();
  }, [id, lawFirmId, company?.id, refreshAnalysesForCompanyTab]);

  useEffect(() => {
    openedCompanyEditFromList.current = false;
  }, [id]);

  useEffect(() => {
    if (loading || !company || openedCompanyEditFromList.current) return;
    const state = location.state as { openCompanyEdit?: boolean } | null | undefined;
    if (!state?.openCompanyEdit) return;
    openedCompanyEditFromList.current = true;
    closeRunSnapshotSlide();
    applyCompanyToEditForm(company);
    setCompanyEditOpen(true);
    navigate(location.pathname, { replace: true, state: null });
  }, [company, loading, location.pathname, location.state, navigate, applyCompanyToEditForm]);

  const companyClientAdapter = useMemo(() => {
    if (!id) return null;
    return createCompanyClientUsersAdapter(lawFirmId, id);
  }, [lawFirmId, id]);

  const firmHeader = useMemo(() => {
    const backBtn = (
      <button
        type="button"
        onClick={() => navigate('/firm/companies')}
        className="inline-flex min-w-0 max-w-full items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-slate-600 transition-colors duration-200 hover:bg-emerald-50/50 hover:text-emerald-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/40 focus-visible:ring-offset-2"
      >
        <ArrowLeft size={17} strokeWidth={2} className="shrink-0" aria-hidden />
        <span className="truncate">Back to companies</span>
      </button>
    );
    if (loading) {
      return <AppPageHeader variant="firmShell" leftContent={backBtn} />;
    }
    if (loadError || !company) {
      return <AppPageHeader variant="firmShell" leftContent={backBtn} />;
    }
    return (
      <AppPageHeader
        variant="firmShell"
        title={company.companyName}
        titleTrailing={
          <button
            type="button"
            onClick={() => navigate('/firm/companies')}
            className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-slate-500 transition-colors duration-200 hover:bg-emerald-50/50 hover:text-emerald-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/40 focus-visible:ring-offset-2"
            aria-label="Back to companies"
            title="Back to companies"
          >
            <ArrowLeft size={17} strokeWidth={2} className="shrink-0" aria-hidden />
          </button>
        }
      />
    );
  }, [loading, loadError, company, navigate]);

  useFirmMainHeaderSlot(firmHeader);

  // Hooks must be called unconditionally (even while loading/error branches return early),
  // so we compute aggregates from safe defaults when `company` isn't ready yet.
  const legacyRuns = company?.analysisRuns ?? [];
  const liveAnalyses = company?.analyses ?? [];

  const firmRunHistoryTableRows = useMemo(() => mapFirmCompanyRunHistoryToTableRows(legacyRuns), [legacyRuns]);
  const firmRunHistoryFilteredRows = useMemo(
    () => filterRunHistoryTableRows(firmRunHistoryTableRows, historicalSearchQuery),
    [firmRunHistoryTableRows, historicalSearchQuery]
  );

  type RunHistoryAgg = {
    count: number;
    lastRunDateRaw: string | null;
    lastRunDateTs: number | null;
  };

  const runHistoryAggByAnalysisName = useMemo(() => {
    const map = new Map<string, RunHistoryAgg>();

    const parseTs = (raw: string | null | undefined): number | null => {
      if (raw == null) return null;
      const d = new Date(raw);
      const t = d.getTime();
      return Number.isNaN(t) ? null : t;
    };

    for (const run of legacyRuns) {
      const key = String(run.analysisName ?? '')
        .trim()
        .toLowerCase();
      if (!key) continue;

      const runTs = parseTs(run.runDate);
      const existing = map.get(key);
      if (!existing) {
        map.set(key, { count: 1, lastRunDateRaw: run.runDate ?? null, lastRunDateTs: runTs });
        continue;
      }

      existing.count += 1;

      // Prefer the latest valid timestamp; if both invalid, keep the most recent raw value.
      if (runTs != null) {
        if (existing.lastRunDateTs == null || runTs > existing.lastRunDateTs) {
          existing.lastRunDateTs = runTs;
          existing.lastRunDateRaw = run.runDate ?? null;
        }
      } else if (existing.lastRunDateTs == null) {
        existing.lastRunDateRaw = run.runDate ?? null;
      }
    }

    return map;
  }, [legacyRuns]);

  // Group legacy runs by `analysisName` (legacy history key), so we can show run history
  // under each live analysis row without backend changes.
  const legacyRunsByAnalysisKey = useMemo(() => {
    const map = new Map<string, typeof legacyRuns>();

    for (const run of legacyRuns) {
      const key = String(run.analysisName ?? '')
        .trim()
        .toLowerCase();
      if (!key) continue;

      const current = map.get(key);
      if (current) current.push(run);
      else map.set(key, [run]);
    }

    // Sort newest first per analysis key for consistent rendering.
    for (const [k, list] of map.entries()) {
      list.sort((a, b) => {
        const ta = new Date(a.runDate ?? '').getTime();
        const tb = new Date(b.runDate ?? '').getTime();
        if (Number.isNaN(ta) && Number.isNaN(tb)) return 0;
        if (Number.isNaN(ta)) return 1;
        if (Number.isNaN(tb)) return -1;
        return tb - ta;
      });
      map.set(k, list);
    }

    return map;
  }, [legacyRuns]);

  if (loading) {
    return (
      <>
        <main className="flex-1 flex flex-col min-h-0 min-w-0">
          <div
            className={`flex-1 overflow-auto pb-8 pt-3 sm:pt-4 flex items-center justify-center ${shellMainPaddingX}`}
          >
            <p className="text-sm text-gray-500">Loading company…</p>
          </div>
        </main>
      </>
    );
  }

  if (loadError || !company) {
    return (
      <>
        <main className="flex-1 flex flex-col min-h-0 min-w-0">
          <div
            className={`flex-1 overflow-auto pb-8 pt-3 sm:pt-4 flex items-center justify-center ${shellMainPaddingX}`}
          >
            <p className="text-gray-500">{loadError ?? 'Company not found.'}</p>
          </div>
        </main>
      </>
    );
  }

  // Legacy run history uses `analysisName`, while live workspace uses `name`.
  // They should match, but sometimes only partially (or with slight formatting differences),
  // so we fall back to an `includes` match when exact lookup returns nothing.
  const getRunAggForAnalysisKey = (analysisKey: string): RunHistoryAgg | undefined => {
    if (!analysisKey) return undefined;

    const exact = runHistoryAggByAnalysisName.get(analysisKey);
    if (exact?.count) return exact;

    let best: RunHistoryAgg | undefined;
    let bestKeyLen = -1;
    for (const [k, v] of runHistoryAggByAnalysisName.entries()) {
      if (!k || v.count <= 0) continue;
      if (k.includes(analysisKey) || analysisKey.includes(k)) {
        // Prefer the longer key as it's more specific.
        if (k.length > bestKeyLen) {
          best = v;
          bestKeyLen = k.length;
        }
      }
    }

    return best;
  };

  const getLegacyRunsForAnalysisKey = (analysisKey: string) => {
    if (!analysisKey) return [];
    const exact = legacyRunsByAnalysisKey.get(analysisKey);
    if (exact && exact.length > 0) return exact;

    let best: typeof legacyRuns | undefined;
    let bestKeyLen = -1;
    for (const [k, v] of legacyRunsByAnalysisKey.entries()) {
      if (!k || v.length === 0) continue;
      if (k.includes(analysisKey) || analysisKey.includes(k)) {
        if (k.length > bestKeyLen) {
          best = v;
          bestKeyLen = k.length;
        }
      }
    }
    return best ?? [];
  };

  const toggleLiveAnalysisExpanded = (analysisRowId: string) => {
    setExpandedLiveAnalysisIds((prev) =>
      prev.includes(analysisRowId) ? prev.filter((x) => x !== analysisRowId) : [...prev, analysisRowId]
    );
  };

  const closeAnalysisSlide = () => {
    setAnalysisSlideOpen(false);
    setAnalysisSlideAnalysisId(null);
  };

  const closeRunSnapshotSlide = () => setRunSnapshotSubject(null);

  const bumpUserDirectoryPanels = () => {
    setDirectoryPanelsCloseKey((k) => k + 1);
  };

  const openRunSnapshotSlide = (subject: RunSnapshotSlideSubject) => {
    setAnalysisSlideOpen(false);
    setAnalysisSlideAnalysisId(null);
    bumpUserDirectoryPanels();
    setCompanyEditOpen(false);
    setRunSnapshotSubject(subject);
  };

  const openAnalysisSlide = (analysisId: string) => {
    // Close other right-side panels so the slide view is the only overlay.
    closeRunSnapshotSlide();
    bumpUserDirectoryPanels();
    setCompanyEditOpen(false);
    setAnalysisSlideAnalysisId(analysisId);
    setAnalysisSlideOpen(true);
  };

  const showInvite = canManageUsers(user?.jwtRole);

  const closeCompanyEdit = () => {
    setCompanyEditOpen(false);
    setCompanyEditError(null);
  };

  const submitCompanyEdit = (e: { preventDefault: () => void }) => {
    e.preventDefault();
    if (!id) return;
    const name = editCompanyName.trim();
    const close = editClosingDate.trim();
    if (!name) {
      setCompanyEditError('Company name is required.');
      return;
    }
    if (!close) {
      setCompanyEditError('Closing date is required.');
      return;
    }
    setCompanyEditSubmitting(true);
    setCompanyEditError(null);
    patchCompany(lawFirmId, id, {
      companyName: name,
      closingDate: close,
      dealStructure: editDealStructure,
      dealRole: editDealRole,
      listingStatus: editListingStatus,
      workStatus: editWorkStatus,
      notes: editNotes.trim(),
    })
      .then(async (updated) => {
        setCompany(updated);
        await refreshCompanies();
        closeCompanyEdit();
      })
      .catch((err: unknown) => {
        setCompanyEditError(err instanceof Error ? err.message : 'Could not save company.');
      })
      .finally(() => setCompanyEditSubmitting(false));
  };

  return (
    <>
      <main className="flex-1 flex flex-col min-h-0 min-w-0">
        <div className={`flex-1 overflow-auto py-5 ${shellMainPaddingX}`}>
          <div className="w-full">
            <section
              className="mb-5 rounded-lg border border-emerald-100/80 bg-gradient-to-br from-emerald-50/50 via-white to-slate-50/40 px-4 py-4 shadow-sm ring-1 ring-gray-100/70"
              aria-labelledby="company-deal-context-heading"
            >
              <h2 id="company-deal-context-heading" className="text-sm font-medium text-gray-900">
                Company Record
              </h2>
              <p className="mt-1 max-w-3xl text-sm leading-relaxed text-gray-500">
                From your company intake: expected closing date and overall deal workflow. These apply to client
                analyses and runs scoped to this company.
              </p>
              <CompanyIntakeBadges
                closingDate={company.closingDate}
                workStatus={company.workStatus}
                className="mt-3"
                closingDateEditable
                onClosingDateSave={async (iso) => {
                  if (!id) return;
                  const updated = await patchCompany(lawFirmId, id, { closingDate: iso });
                  setCompany(updated);
                  await refreshCompanies();
                }}
              />
            </section>
            <nav
              className="mb-5 flex flex-wrap gap-1 border-b border-gray-200/90"
              role="tablist"
              aria-label="Company sections"
            >
              <button
                type="button"
                role="tab"
                id="company-tab-analyses"
                aria-selected={activeTab === 'analyses'}
                aria-controls="company-panel-analyses"
                onClick={() => {
                  setActiveTab('analyses');
                  refreshAnalysesForCompanyTab();
                }}
                title="Workspace 280G Analyses for this company"
                className={`-mb-px flex min-h-[2.75rem] items-center gap-2 border-b-2 px-1 pb-3 pt-1 text-sm transition-colors sm:mr-6 ${
                  activeTab === 'analyses'
                    ? 'border-emerald-600 font-medium text-emerald-900'
                    : 'border-transparent font-medium text-gray-500 hover:border-gray-300 hover:text-gray-800'
                }`}
              >
                <LineChart
                  size={17}
                  strokeWidth={activeTab === 'analyses' ? 2.25 : 1.75}
                  className={`shrink-0 ${activeTab === 'analyses' ? 'text-emerald-700' : 'text-gray-400'}`}
                  aria-hidden
                />
                280G Analyses
              </button>
              <button
                type="button"
                role="tab"
                id="company-tab-historical-runs"
                aria-selected={activeTab === 'historicalRuns'}
                aria-controls="company-panel-historical-runs"
                onClick={() => {
                  setActiveTab('historicalRuns');
                  refreshAnalysesForCompanyTab();
                }}
                title="Legacy AnalysisRuns history for this company"
                className={`-mb-px flex min-h-[2.75rem] items-center gap-2 border-b-2 px-1 pb-3 pt-1 text-sm transition-colors sm:mr-6 ${
                  activeTab === 'historicalRuns'
                    ? 'border-emerald-600 font-medium text-emerald-900'
                    : 'border-transparent font-medium text-gray-500 hover:border-gray-300 hover:text-gray-800'
                }`}
              >
                <History
                  size={17}
                  strokeWidth={activeTab === 'historicalRuns' ? 2.25 : 1.75}
                  className={`shrink-0 ${activeTab === 'historicalRuns' ? 'text-emerald-700' : 'text-gray-400'}`}
                  aria-hidden
                />
                Historical Runs
              </button>
              <button
                type="button"
                role="tab"
                id="company-tab-company-users"
                aria-selected={activeTab === 'companyUsers'}
                aria-controls="company-panel-company-users"
                onClick={() => setActiveTab('companyUsers')}
                title="Client logins (company users) for 280G"
                className={`-mb-px flex min-h-[2.75rem] items-center gap-2 border-b-2 px-1 pb-3 pt-1 text-sm transition-colors ${
                  activeTab === 'companyUsers'
                    ? 'border-emerald-600 font-medium text-emerald-900'
                    : 'border-transparent font-medium text-gray-500 hover:border-gray-300 hover:text-gray-800'
                }`}
              >
                <Users
                  size={17}
                  strokeWidth={activeTab === 'companyUsers' ? 2.25 : 1.75}
                  className={`shrink-0 ${activeTab === 'companyUsers' ? 'text-emerald-700' : 'text-gray-400'}`}
                  aria-hidden
                />
                Company Users
              </button>
            </nav>

            {activeTab === 'analyses' ? (
              <div id="company-panel-analyses" role="tabpanel" aria-labelledby="company-tab-analyses">
                <div className="mb-6 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
                  <div className="border-b border-gray-200 bg-gray-50/80 px-5 py-4">
                    <h2 className="text-base font-medium text-gray-900">Workspace Analyses</h2>
                    <p className="mt-1 max-w-3xl text-sm leading-relaxed text-gray-500">
                      Analyses created in the client app for this company. Open a row to review details, or expand for
                      legacy run history.
                    </p>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[980px]">
                      <thead className="sticky top-0 z-10">
                        <tr className="border-b border-gray-200 bg-gray-50 shadow-[0_1px_0_0_rgb(229_231_235)]">
                          <th className="min-w-[200px] px-5 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                            Analysis
                          </th>
                          <th className="px-5 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 tabular-nums">
                            Individuals
                          </th>
                          <th className="px-5 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                            Deal price
                          </th>
                          <th className="px-5 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                            AFR
                          </th>
                          <th className="min-w-[150px] px-5 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                            Status
                          </th>
                          <th className="w-32 px-5 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 tabular-nums">
                            Runs
                          </th>
                          <th className="w-44 px-5 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                            Last run date
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {liveAnalyses.length === 0 ? (
                          <tr>
                            <td colSpan={7} className="px-5 py-10 text-center text-sm text-gray-500">
                              <p className="font-medium text-gray-700">No analyses yet</p>
                              <p className="mt-1 text-xs text-gray-500">
                                They appear here when client users create 280G analyses linked to this company.
                              </p>
                            </td>
                          </tr>
                        ) : (
                          liveAnalyses.map((row) => {
                            const updatedLabel = formatFirmAnalysisUpdatedSubtitle(row.updatedAt);
                            const dealPriceFormatted = formatFirmAnalysisDealPrice(row.dealPrice);
                            const afrDisplay = formatFirmAnalysisAfr(row.afr);
                            const statusLabel = row.status?.trim() || 'Not started';
                            const analysisKey = String(row.name ?? '')
                              .trim()
                              .toLowerCase();
                            const runAgg = getRunAggForAnalysisKey(analysisKey);
                            const runResultsCount = runAgg?.count ?? 0;
                            const lastRunDateRaw = runAgg?.lastRunDateRaw ?? null;
                            const legacyRunsForThisAnalysis = getLegacyRunsForAnalysisKey(analysisKey);
                            const canExpand = legacyRunsForThisAnalysis.length > 0;
                            const isExpanded = expandedLiveAnalysisIds.includes(row.id);

                            const lastRunDateDisplay =
                              lastRunDateRaw && !Number.isNaN(new Date(lastRunDateRaw).getTime())
                                ? new Date(lastRunDateRaw).toLocaleString(undefined, {
                                    year: 'numeric',
                                    month: 'short',
                                    day: '2-digit',
                                    hour: 'numeric',
                                    minute: '2-digit',
                                  })
                                : (lastRunDateRaw ?? '—');

                            return (
                              <React.Fragment key={row.id}>
                                <tr className="group transition-colors hover:bg-gray-50/90">
                                  <td className="align-top px-5 py-4">
                                    <div className="flex items-start gap-3">
                                      <FirmLiveAnalysisExpandChevron
                                        canExpand={canExpand}
                                        isExpanded={isExpanded}
                                        ariaControlsId={`analysis-run-history-${row.id}`}
                                        onToggle={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          if (!canExpand) return;
                                          toggleLiveAnalysisExpanded(row.id);
                                        }}
                                      />
                                      <div className="min-w-0">
                                        <div className="inline-flex flex-wrap items-center gap-x-2 gap-y-0.5">
                                          <button
                                            type="button"
                                            onClick={(e) => {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              openAnalysisSlide(row.id);
                                            }}
                                            className="min-w-0 max-w-[min(100%,20rem)] truncate rounded-sm text-left font-medium text-emerald-700 hover:text-emerald-900 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-600/50"
                                          >
                                            {row.name?.trim() || 'Untitled analysis'}
                                          </button>
                                          <FirmAnalysisPersistedLockMark
                                            analysisPersistedStatus={row.analysisPersistedStatus}
                                            withAriaLabel
                                          />
                                        </div>
                                        {updatedLabel ? (
                                          <p className="mt-1 text-[11px] leading-snug text-gray-400">
                                            Updated {updatedLabel}
                                          </p>
                                        ) : null}
                                      </div>
                                    </div>
                                  </td>
                                  <td className="align-top px-5 py-4 text-right text-sm tabular-nums text-gray-700">
                                    {row.individualsCount}
                                  </td>
                                  <td className="align-top px-5 py-4 text-right text-sm tabular-nums text-gray-700">
                                    {dealPriceFormatted}
                                  </td>
                                  <td className="align-top px-5 py-4 text-right text-sm tabular-nums text-gray-700">
                                    {afrDisplay}
                                  </td>
                                  <td className="align-top px-5 py-4">
                                    <FirmLiveAnalysisWorkflowStatusBadge
                                      workflowProgress={row.workflowProgress}
                                      statusLabel={statusLabel}
                                    />
                                  </td>
                                  <td className="align-top px-5 py-4 text-right text-sm tabular-nums text-gray-700">
                                    {runResultsCount}
                                  </td>
                                  <td className="align-top whitespace-nowrap px-5 py-4 text-right text-sm text-gray-700">
                                    {lastRunDateDisplay}
                                  </td>
                                </tr>
                                {isExpanded ? (
                                  <tr className="bg-gray-50/60">
                                    <td colSpan={7} className="px-5 py-3">
                                      <FirmLiveAnalysisRunHistoryPanel
                                        panelId={`analysis-run-history-${row.id}`}
                                        title="Run history"
                                        legacyRuns={legacyRunsForThisAnalysis}
                                        onViewSnapshot={(r) =>
                                          openRunSnapshotSlide(runHistoryRowToSnapshotSlideSubject(r))
                                        }
                                      />
                                    </td>
                                  </tr>
                                ) : null}
                              </React.Fragment>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            ) : null}

            {activeTab === 'historicalRuns' ? (
              <div id="company-panel-historical-runs" role="tabpanel" aria-labelledby="company-tab-historical-runs">
                <RunHistoryPanel
                  error={historicalRunsError}
                  loading={historicalRunsLoading}
                  loadingCardCompact
                  totalRowCount={firmRunHistoryTableRows.length}
                  searchQuery={historicalSearchQuery}
                  onSearchChange={setHistoricalSearchQuery}
                  searchInputId="firm-historical-runs-search"
                  displayRows={firmRunHistoryFilteredRows}
                  onViewSnapshot={(r) => openRunSnapshotSlide(runHistoryRowToSnapshotSlideSubject(r))}
                  emptyState={
                    <ClientEmptyStateCard icon={FileClock} title="No run history recorded yet.">
                      <p>
                        Click <strong>Run 280G Results</strong> on the 280G results page to persist snapshots. Use{' '}
                        <strong>View</strong> on a row below when a snapshot is available.
                      </p>
                    </ClientEmptyStateCard>
                  }
                />
              </div>
            ) : null}

            {activeTab === 'companyUsers' && companyClientAdapter ? (
              <div id="company-panel-company-users" role="tabpanel" aria-labelledby="company-tab-company-users">
                <ManagedUserDirectoryPanel
                  preset="companyClient"
                  adapter={companyClientAdapter}
                  canManage={showInvite}
                  enabled={activeTab === 'companyUsers'}
                  panelsCloseKey={directoryPanelsCloseKey}
                  includeMainChrome={false}
                  compactTable
                  idPrefix={`company-${id ?? 'users'}`}
                  intro={
                    <div className="mb-4 rounded-xl border border-slate-200 bg-slate-50/80 px-4 py-3 sm:px-5 sm:py-4">
                      <div className="flex min-w-0 gap-3">
                        <div
                          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-violet-100 text-violet-700"
                          title="Client accounts for this company"
                        >
                          <Users size={20} strokeWidth={1.75} aria-hidden />
                        </div>
                        <div className="min-w-0">
                          <h3 className="text-sm font-medium text-gray-900">Client accounts (280G)</h3>
                          <p className="mt-1 text-sm leading-relaxed text-gray-600">
                            Users below are <span className="font-medium text-gray-800">client</span> logins linked to
                            this company and your law firm tenant. Law firm staff are listed under{' '}
                            <span className="font-medium text-gray-800">Law firm users</span> in the sidebar.
                          </p>
                        </div>
                      </div>
                    </div>
                  }
                />
              </div>
            ) : null}
          </div>
        </div>
      </main>

      {analysisSlideOpen ? (
        <div className="fixed inset-0 z-50 transition-opacity duration-200 opacity-100">
          <div
            className="absolute inset-0 bg-black/40 transition-opacity duration-200"
            onClick={closeAnalysisSlide}
            aria-hidden
          />
          <div className="absolute top-0 right-0 flex h-full w-full max-w-[80%] w-[80%] flex-col bg-white shadow-xl transition-transform duration-300 ease-out translate-x-0">
            <div className="flex min-h-0 flex-1 flex-col">
              <div className="flex flex-shrink-0 items-center justify-between border-b border-gray-200 px-6 py-4">
                <div className="min-w-0 flex-1">
                  <div className="flex min-w-0 items-center gap-2">
                    <h3 className="font-medium text-gray-900 text-xl truncate">
                      {analysisSlideAnalysisId
                        ? liveAnalyses.find((a) => a.id === analysisSlideAnalysisId)?.name?.trim() ||
                          'Untitled analysis'
                        : ''}
                    </h3>
                    {analysisSlideAnalysisId
                      ? (() => {
                          const selected = liveAnalyses.find((a) => a.id === analysisSlideAnalysisId);
                          const slideLocked = isAnalysisLocked(selected?.analysisPersistedStatus);
                          return (
                            <span
                              className={`inline-flex shrink-0 items-center ${slideLocked ? 'text-red-600' : 'text-gray-400'}`}
                              title={
                                slideLocked
                                  ? 'Final: workflow edits and new runs are disabled in the client app.'
                                  : 'Editable: analysis is not final.'
                              }
                              aria-hidden
                            >
                              {slideLocked ? <Lock size={18} /> : <Unlock size={18} />}
                            </span>
                          );
                        })()
                      : null}
                  </div>
                  {analysisSlideAnalysisId
                    ? (() => {
                        const selected = liveAnalyses.find((a) => a.id === analysisSlideAnalysisId);
                        const statusLabel = selected?.status?.trim() || 'Not started';
                        const normalized = statusLabel.toLowerCase();
                        const badgeVariant =
                          normalized === 'complete' ? 'success' : normalized === 'in progress' ? 'warning' : 'default';
                        return (
                          <div className="mt-1">
                            <Badge variant={badgeVariant}>{statusLabel}</Badge>
                          </div>
                        );
                      })()
                    : null}
                </div>
                <button
                  type="button"
                  onClick={closeAnalysisSlide}
                  className="rounded-lg p-2 text-gray-400 hover:text-gray-600"
                  aria-label="Close"
                  title="Close"
                >
                  <XCircle size={20} aria-hidden />
                </button>
              </div>
              <div className="flex flex-shrink-0 px-6 pt-3">
                <InfoBanner variant="danger" showIcon>
                  Review mode: changes you make here are not saved. Use the app&apos;s Save/Continue actions to persist.
                </InfoBanner>
              </div>
              <div className="flex-1 overflow-y-auto p-6">
                {analysisSlideAnalysisId ? (
                  <CompanyProvider>
                    <AnalysisSlideWorkflow analysisId={analysisSlideAnalysisId} />
                  </CompanyProvider>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <RunSnapshotSlideOver subject={runSnapshotSubject} onClose={closeRunSnapshotSlide} />

      <div
        className={`fixed inset-0 z-50 transition-opacity duration-200 ${
          companyEditOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
      >
        <div
          className="absolute inset-0 bg-black/40 transition-opacity duration-200"
          onClick={closeCompanyEdit}
          aria-hidden
        />
        <div
          className={`absolute top-0 right-0 flex h-full w-full max-w-lg flex-col bg-white shadow-xl transition-transform duration-300 ease-out ${
            companyEditOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          <form className="flex min-h-0 flex-1 flex-col" onSubmit={submitCompanyEdit}>
            <div className="flex flex-shrink-0 items-center justify-between gap-3 border-b border-gray-200 px-6 py-4">
              <div className="flex min-w-0 items-center gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-emerald-700">
                  <Building2 size={20} aria-hidden />
                </span>
                <h3 className="font-medium text-gray-900">Edit Company</h3>
              </div>
              <button
                type="button"
                onClick={closeCompanyEdit}
                className="shrink-0 rounded-lg p-2 text-gray-400 hover:text-gray-600"
                aria-label="Close"
              >
                <XCircle size={20} />
              </button>
            </div>
            <div className="flex-1 space-y-6 overflow-y-auto p-6">
              <p className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2.5 text-sm text-gray-600">
                Changes apply to this company record and update what client analyses show when linked to this company.
              </p>
              {companyEditError ? (
                <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900">
                  {companyEditError}
                </div>
              ) : null}
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="firm-detail-edit-name">
                  Company name
                </label>
                <input
                  id="firm-detail-edit-name"
                  type="text"
                  value={editCompanyName}
                  onChange={(e) => setEditCompanyName(e.target.value)}
                  autoComplete="organization"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm outline-none focus:border-emerald-600 focus:ring-2 focus:ring-emerald-600/40"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="firm-detail-edit-closing">
                  Closing date
                </label>
                <input
                  id="firm-detail-edit-closing"
                  type="date"
                  value={editClosingDate}
                  onChange={(e) => setEditClosingDate(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm outline-none focus:border-emerald-600 focus:ring-2 focus:ring-emerald-600/40"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="firm-detail-edit-structure">
                  Deal structure
                </label>
                <select
                  id="firm-detail-edit-structure"
                  value={editDealStructure}
                  onChange={(e) => setEditDealStructure(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-emerald-600 focus:ring-2 focus:ring-emerald-600/40"
                >
                  {DEAL_STRUCTURE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="firm-detail-edit-role">
                  Company&apos;s role in the deal
                </label>
                <select
                  id="firm-detail-edit-role"
                  value={editDealRole}
                  onChange={(e) => setEditDealRole(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-emerald-600 focus:ring-2 focus:ring-emerald-600/40"
                >
                  {DEAL_ROLE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="firm-detail-edit-listing">
                  Listing
                </label>
                <select
                  id="firm-detail-edit-listing"
                  value={editListingStatus}
                  onChange={(e) => setEditListingStatus(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-emerald-600 focus:ring-2 focus:ring-emerald-600/40"
                >
                  {LISTING_STATUS_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="firm-detail-edit-work-status">
                  Overall status
                </label>
                <p className="mb-1.5 text-xs text-gray-500">
                  Deal / 280G workflow for this company (separate from client user active/inactive).
                </p>
                <select
                  id="firm-detail-edit-work-status"
                  value={editWorkStatus}
                  onChange={(e) => setEditWorkStatus(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-emerald-600 focus:ring-2 focus:ring-emerald-600/40"
                >
                  {WORK_STATUS_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="firm-detail-edit-notes">
                  Notes <span className="font-normal text-gray-400">(optional)</span>
                </label>
                <textarea
                  id="firm-detail-edit-notes"
                  value={editNotes}
                  onChange={(e) => setEditNotes(e.target.value)}
                  rows={4}
                  placeholder="Anything that affects scope or what to collect next…"
                  className="min-h-[5rem] w-full resize-y rounded-lg border border-gray-300 px-3 py-2.5 text-sm outline-none focus:border-emerald-600 focus:ring-2 focus:ring-emerald-600/40"
                />
              </div>
            </div>
            <div className="flex flex-shrink-0 flex-wrap items-center justify-between gap-2 border-t border-gray-200 px-6 py-4">
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={closeCompanyEdit}
                  className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                  disabled={companyEditSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => {
                    closeCompanyEdit();
                    void refreshCompanies();
                    navigate('/firm/companies');
                  }}
                  className="rounded-lg border border-emerald-200 bg-emerald-50/80 px-4 py-2 text-sm font-medium text-emerald-900 hover:bg-emerald-100/90"
                  disabled={companyEditSubmitting}
                  title="Leave this page and open the companies table"
                >
                  Companies list
                </button>
              </div>
              <button
                type="submit"
                className="rounded-lg px-4 py-2 text-sm font-medium btn-primary disabled:opacity-60"
                disabled={companyEditSubmitting}
              >
                {companyEditSubmitting ? 'Saving…' : 'Save company'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}
